/* eslint-disable complexity */
/* eslint-disable max-lines */
import {
    NinchatRatingFace1Icon,
    NinchatRatingFace2Icon,
    NinchatRatingFace3Icon,
    NinchatRatingFace4Icon,
    NinchatRatingFace5Icon,
} from 'components/Icon'
import React, {useContext} from 'react'

import {
    customIconWrapper, emojiButton,
    npsButton, npsContent, npsLabel, npsLineBreak, ratingItem, ratingWrapper,
} from './styles'

import {
    useAppDispatch, useAppSelector,
} from 'hooks'


import {EmbedContext} from 'components/EmbedContext'
import {NinchatMessages} from 'api/types'
import {ThemeContext} from 'stitches/ThemeProvider/ThemeProvider'

import {
    embedTheme,
} from 'stitches/themes'

import {sendMessage} from 'api/action/sendMessage'

import {
    styled,
} from 'stitches/config'

import {useTranslation} from 'react-i18next'

const StyledRatingWrapper = styled('div', ratingWrapper)
const StyledRatingItem = styled('div', ratingItem)
const StyledEmojiButton = styled('button', emojiButton)

const StyledNpsWrapper = styled('div', {})
const StyledNpsContent = styled('div', npsContent)
const StyledNpsButton = styled('button', npsButton)
const StyledLabel = styled('div', npsLabel)
const StyledCustomIconWrapper = styled('div', customIconWrapper)
const StyledNpsLineBreak = styled('div', npsLineBreak)

interface IconDetails {
    src?: string,
    alt?: string,
    width?: number,
    height?: number,
    svg?: string,
}

export interface QuestionnaireRatingProps {
    lightBotAnswer?: number
    colorfull?: boolean
    target: string,
    type: 'emoji-5' | 'emoji-3' | 'nps'
    name: string
    npsDetails?: {
        numberOfItems: number
        startingValue: number
        topLabel: string
        bottomLabel: string
    },
    icons?: {
        good?: IconDetails,
        goodSelected?: IconDetails,
        averageGood?: IconDetails,
        averageGoodSelected?: IconDetails,
        average?: IconDetails,
        averageSelected?: IconDetails,
        averageBad?: IconDetails,
        averageBadSelected?: IconDetails,
        bad?: IconDetails,
        badSelected?: IconDetails,
    }
    align?: 'center' | 'right'
    readableName?: string
    groupName?: string
    reverse?: boolean
    onSendRating: (target: string) => void
    onChange: (params: {name: string, value: string, readableName?: string, groupName?: string}) => void
}

export const QuestionnaireRating = (props: QuestionnaireRatingProps) => {
    const {
        type, reverse, npsDetails, onChange, onSendRating, name, lightBotAnswer,
        readableName, groupName, icons, align, target, ...other
    } = props

    const [selectedValue, setSelectedValue] = React.useState<number>()
    const [hoveredValue, setHoveredValue] = React.useState<number>()
    const theme = useContext(ThemeContext) as typeof embedTheme
    const userId = useAppSelector(({ninchat}) => ninchat.user_id)
    const embed = useContext(EmbedContext)
    const dispatch = useAppDispatch()
    const {t} = useTranslation()

    const sendRating = async (rating: string) => {
        await dispatch(sendMessage({
            channel_id: embed?.embedState.lastChannelId,
            message_fold: true,
            message_privileged: false,
            message_recipient_ids: [userId || ''],
            message_type: NinchatMessages.NinchatMessageTypes.NinchatMetadataMessage,
            payload: [JSON.stringify({data: {rating}})],
        }))

        onSendRating(target)
    }

    const handleSelect = (value: number) => () => {
        setSelectedValue(value)
        onChange({
            groupName,
            name,
            readableName,
            value: value.toString(),
        })

        sendRating(value.toString())
    }

    const getImage = (icon?: IconDetails) => {
        if (icon?.svg) {
            return (
                <StyledCustomIconWrapper
                    css={{
                        $$height: `${icon?.height || 50}px`,
                        $$width: `${icon?.width || 50}px`,
                    }}
                    dangerouslySetInnerHTML={{__html: icon.svg}}
                />)
        }
        if (icon?.src) {
            return (
                <StyledCustomIconWrapper css={{
                    $$height: `${icon?.height || 50}px`,
                    $$width: `${icon?.width || 50}px`,
                }}
                >
                    <img alt={icon?.alt || ''} src={icon.src} />
                </StyledCustomIconWrapper>)
        }
        return null
    }

    const ratingLabels: any = {
        emoji3: {
            '-1': t('Bad'),
            '0': t('Average'),
            '1': t('Good'),
        },
        emoji5: {
            1: t('Very bad'),
            2: t('Bad'),
            3: t('Average'),
            4: t('Good'),
            5: t('Very good'),
        },
    }

    const emoji3 = [
        {
            icon: getImage(selectedValue === -1 || hoveredValue === -1 ? icons?.badSelected : icons?.bad)
                ? getImage(selectedValue === -1 || hoveredValue === -1 ? icons?.badSelected : icons?.bad)
                : (
                    <NinchatRatingFace1Icon
                        {...(selectedValue === -1 || hoveredValue === -1
                            ? {
                                background: theme.colors?.questionnaireEmojiBad?.value,
                                border: theme.colors?.questionnaireEmojiBad?.value,
                                color: theme.colors?.questionnaireEmoji?.value,
                            }
                            : {
                                background: theme.colors?.questionnaireEmojiBackground?.value,
                                border: theme.colors?.questionnaireEmojiBad?.value,
                                color: theme.colors?.questionnaireEmojiBad?.value,
                            })}
                        size={50}
                    />),
            value: -1,
        },
        {
            icon: getImage(selectedValue === 0 || hoveredValue === 0 ? icons?.averageSelected : icons?.average)
                ? getImage(selectedValue === 0 || hoveredValue === 0 ? icons?.averageSelected : icons?.average)
                : (
                    <NinchatRatingFace3Icon
                        {...(selectedValue === 0 || hoveredValue === 0
                            ? {
                                background: theme.colors?.questionnaireEmojiAverage?.value,
                                border: theme.colors?.questionnaireEmojiAverage?.value,
                                color: theme.colors?.questionnaireEmoji?.value,
                            }
                            : {
                                background: theme.colors?.questionnaireEmojiBackground?.value,
                                border: theme.colors?.questionnaireEmojiAverage?.value,
                                color: theme.colors?.questionnaireEmojiAverage?.value,
                            })}
                        size={50}
                    />),
            value: 0,
        },
        {
            icon: getImage(selectedValue === 1 || hoveredValue === 1 ? icons?.goodSelected : icons?.good)
                ? getImage(selectedValue === 1 || hoveredValue === 1 ? icons?.goodSelected : icons?.good)
                : (
                    <NinchatRatingFace5Icon
                        {...(selectedValue === 1 || hoveredValue === 1
                            ? {
                                background: theme.colors?.questionnaireEmojiGood?.value,
                                border: theme.colors?.questionnaireEmojiGood?.value,
                                color: theme.colors?.questionnaireEmoji?.value,
                            }
                            : {
                                background: theme.colors?.questionnaireEmojiBackground?.value,
                                border: theme.colors?.questionnaireEmojiGood?.value,
                                color: theme.colors?.questionnaireEmojiGood?.value,
                            })}
                        size={50}
                    />),
            value: 1,
        },
    ]

    const emoji5 = [
        {
            icon: getImage(selectedValue === 1 || hoveredValue === 1 ? icons?.badSelected : icons?.bad)
                ? getImage(selectedValue === 1 || hoveredValue === 1 ? icons?.badSelected : icons?.bad)
                : (
                    <NinchatRatingFace1Icon
                        size={50}
                        {
                            ...(selectedValue === 1 || hoveredValue === 1
                                ? {
                                    background: theme.colors?.questionnaireEmojiBad?.value,
                                    border: theme.colors?.questionnaireEmojiBad?.value,
                                    color: theme.colors?.questionnaireEmoji?.value,
                                }
                                : {
                                    background: theme.colors?.questionnaireEmojiBackground?.value,
                                    border: theme.colors?.questionnaireEmojiBad?.value,
                                    color: theme.colors?.questionnaireEmojiBad?.value,
                                })}
                    />),
            value: 1,
        },
        {
            icon:
                getImage(selectedValue === 2 || hoveredValue === 2 ? icons?.averageBadSelected : icons?.averageBad)
                    ? getImage(selectedValue === 2 || hoveredValue === 2 ? icons?.averageBadSelected : icons?.averageBad)
                    :
                    <NinchatRatingFace2Icon
                        {...(selectedValue === 2 || hoveredValue === 2
                            ? {
                                background: theme.colors?.questionnaireEmojiAverageBad?.value,
                                border: theme.colors?.questionnaireEmojiAverageBad?.value,
                                color: theme.colors?.questionnaireEmoji?.value,
                            }
                            : {
                                background: theme.colors?.questionnaireEmojiBackground?.value,
                                border: theme.colors?.questionnaireEmojiAverageBad?.value,
                                color: theme.colors?.questionnaireEmojiAverageBad?.value,
                            })}
                        size={50}
                    />,
            value: 2,
        },
        {
            icon:
                getImage(selectedValue === 3 || hoveredValue === 3 ? icons?.averageSelected : icons?.average)
                    ? getImage(selectedValue === 3 || hoveredValue === 3 ? icons?.averageSelected : icons?.average)
                    :
                    <NinchatRatingFace3Icon
                        {...(selectedValue === 3 || hoveredValue === 3
                            ? {
                                background: theme.colors?.questionnaireEmojiAverage?.value,
                                border: theme.colors?.questionnaireEmojiAverage?.value,
                                color: theme.colors?.questionnaireEmoji?.value,
                            }
                            : {
                                background: theme.colors?.questionnaireEmojiBackground?.value,
                                border: theme.colors?.questionnaireEmojiAverage?.value,
                                color: theme.colors?.questionnaireEmojiAverage?.value,
                            })}
                        size={50}
                    />,
            value: 3,
        },
        {
            icon:
                getImage(selectedValue === 4 || hoveredValue === 4 ? icons?.averageGoodSelected : icons?.averageGood)
                    ? getImage(selectedValue === 4 || hoveredValue === 4 ? icons?.averageGoodSelected : icons?.averageGood)
                    :
                    <NinchatRatingFace4Icon
                        {...(selectedValue === 4 || hoveredValue === 4
                            ? {
                                background: theme.colors?.questionnaireEmojiAverageGood?.value,
                                border: theme.colors?.questionnaireEmojiAverageGood?.value,
                                color: theme.colors?.questionnaireEmoji?.value,
                            }
                            : {
                                background: theme.colors?.questionnaireEmojiBackground?.value,
                                border: theme.colors?.questionnaireEmojiAverageGood?.value,
                                color: theme.colors?.questionnaireEmojiAverageGood?.value,
                            })}
                        size={50}
                    />,
            value: 4,
        },
        {
            icon:
                getImage(selectedValue === 5 || hoveredValue === 5 ? icons?.goodSelected : icons?.good)
                    ? getImage(selectedValue === 5 || hoveredValue === 5 ? icons?.goodSelected : icons?.good)
                    :
                    <NinchatRatingFace5Icon
                        {...(selectedValue === 5 || hoveredValue === 5
                            ? {
                                background: theme.colors?.questionnaireEmojiGood?.value,
                                border: theme.colors?.questionnaireEmojiGood?.value,
                                color: theme.colors?.questionnaireEmoji?.value,
                            }
                            : {
                                background: theme.colors?.questionnaireEmojiBackground?.value,
                                border: theme.colors?.questionnaireEmojiGood?.value,
                                color: theme.colors?.questionnaireEmojiGood?.value,
                            })
                        }
                        size={50}
                    />,
            value: 5,
        },
    ]

    const renderAnswerEmoji = (value: number) => {
        const answerEmojiColor = '#ffffff'
        if (type === 'emoji-3') {
            switch (value) {
                case -1:
                    return getImage(icons?.badSelected) || <NinchatRatingFace1Icon background='transparent' border={answerEmojiColor} color={answerEmojiColor} size={30} />
                case 0:
                    return getImage(icons?.averageSelected) || <NinchatRatingFace3Icon background='transparent' border={answerEmojiColor} color={answerEmojiColor} size={30} />
                case 1:
                    return getImage(icons?.goodSelected) || <NinchatRatingFace5Icon background='transparent' border={answerEmojiColor} color={answerEmojiColor} size={30} />
                default:
                    return null
            }
        }
        if (type === 'emoji-5') {
            switch (value) {
                case 1:
                    return getImage(icons?.badSelected) || <NinchatRatingFace1Icon background='transparent' border={answerEmojiColor} color={answerEmojiColor} size={30} />
                case 2:
                    return getImage(icons?.averageBadSelected) || <NinchatRatingFace2Icon background='transparent' border={answerEmojiColor} color={answerEmojiColor} size={30} />
                case 3:
                    return getImage(icons?.averageSelected) || <NinchatRatingFace3Icon background='transparent' border={answerEmojiColor} color={answerEmojiColor} size={30} />
                case 4:
                    return getImage(icons?.averageGoodSelected) || <NinchatRatingFace4Icon background='transparent' border={answerEmojiColor} color={answerEmojiColor} size={30} />
                case 5:
                    return getImage(icons?.goodSelected) || <NinchatRatingFace5Icon background='transparent' border={answerEmojiColor} color={answerEmojiColor} size={30} />
                default:
                    return null
            }
        }
        if (type === 'nps') {
            return value
        }
        return null
    }

    if (lightBotAnswer !== undefined) {
        return (
            <div>
                {renderAnswerEmoji(Number(lightBotAnswer))}
            </div>
        )
    }

    return (
        <StyledRatingWrapper align={align} {...other}>
            {type === 'emoji-3' &&
                    (reverse ? [...emoji3].reverse() : emoji3).map((item) => (
                        <StyledRatingItem key={item.value}>
                            <StyledEmojiButton
                                aria-label={ratingLabels?.emoji3?.[item.value]}
                                onClick={handleSelect(item.value)}
                                onMouseEnter={() => setHoveredValue(item.value)}
                                onMouseLeave={() => setHoveredValue(undefined)}
                                type='button'
                            >
                                {item.icon}
                            </StyledEmojiButton>
                        </StyledRatingItem>
                    ))}
            {type === 'emoji-5' &&
                    (reverse ? [...emoji5].reverse() : emoji5).map((item) => (
                        <StyledRatingItem key={item.value}>
                            <StyledEmojiButton
                                aria-label={ratingLabels?.emoji5?.[item.value]}
                                onClick={handleSelect(item.value)}
                                onMouseEnter={() => setHoveredValue(item.value)}
                                onMouseLeave={() => setHoveredValue(undefined)}
                                type='button'
                            >
                                {item.icon}
                            </StyledEmojiButton>
                        </StyledRatingItem>
                    ))}
            {type === 'nps' && (
                <StyledNpsWrapper>
                    {npsDetails?.topLabel ? <StyledLabel>{npsDetails?.topLabel}</StyledLabel> : null}
                    <StyledNpsContent>
                        {(reverse
                            ? Array.from({length: npsDetails?.numberOfItems || 10}, (_v, i) => i).reverse()
                            : Array.from({length: npsDetails?.numberOfItems || 10}, (_v, i) => i)
                        ).map((item, index) => {
                            const value = item + (npsDetails?.startingValue || 0)
                            return (
                                <>
                                    <StyledNpsButton
                                        key={value}
                                        onClick={handleSelect(value)}
                                        onMouseEnter={() => setHoveredValue(value)}
                                        onMouseLeave={() => setHoveredValue(undefined)}
                                        selected={value === selectedValue || value === hoveredValue}
                                        type='button'
                                    >
                                        {value}
                                    </StyledNpsButton>
                                    {index === 5 ? <StyledNpsLineBreak /> : null}
                                </>
                            )
                        })}
                    </StyledNpsContent>
                    {npsDetails?.bottomLabel ? <StyledLabel type='bottom'>{npsDetails?.bottomLabel}</StyledLabel> : null}

                </StyledNpsWrapper>
            )}

        </StyledRatingWrapper>
    )
}

QuestionnaireRating.defaultProps = {
    align: null,
    colorfull: false,
    groupName: '',
    icons: {},
    lightBotAnswer: undefined,
    npsDetails: {},
    readableName: '',
    reverse: false,
}
